import { Suspense, useEffect, useState, type ReactNode } from "react";
import { createBrowserRouter, type DataRouter } from "react-router";
import { RouterProvider } from "react-router/dom";

import { AssetApiProvider } from "@skilitics/assets";
import { ErrorBoundary, LogoLoading } from "@skilitics/designer-ui-components";

import { useAssetApi } from "../../api";
import { AuthWrapper } from "../../auth/AuthWrapper.tsx";
import { LandingPage } from "../LandingPage.tsx";
import { LandingPageSettingsProvider } from "../LandingPageSettingsProvider";
import {
  deliveryRoute,
  inviteRoute,
  moduleRoute,
  moduleSetRoute,
  projectsRoute,
  recoverRoute,
  settingsRoute,
} from "../routes/index.ts";
import { CreateModuleSettingsProvider, DuplicateModuleSettingsProvider, FilterSettingsProvider } from "../wizards";
import { getContext } from "./context.ts";
import { navigateToModules } from "./middleware.ts";
import { NotFoundPage } from "./NotFoundPage.tsx";
import { RouterErrorBoundary } from "./RouterErrorBoundary.tsx";

export const AppRouter = () => {
  const assetApi = useAssetApi();

  return (
    <LocationTrackingErrorBoundary router={appRouter}>
      <LandingPageSettingsProvider>
        <DuplicateModuleSettingsProvider>
          <CreateModuleSettingsProvider>
            <FilterSettingsProvider>
              <AssetApiProvider api={assetApi}>
                <Suspense fallback={<LogoLoading />}>
                  <RouterProvider router={appRouter} />
                </Suspense>
              </AssetApiProvider>
            </FilterSettingsProvider>
          </CreateModuleSettingsProvider>
        </DuplicateModuleSettingsProvider>
      </LandingPageSettingsProvider>
    </LocationTrackingErrorBoundary>
  );
};

export const appRouter = createBrowserRouter(
  [
    {
      ErrorBoundary: RouterErrorBoundary,
      Component: AuthWrapper,
      children: [
        // Module route (actual designer) doesn't use LandingPage layout
        moduleRoute,
        {
          Component: LandingPage,
          children: [
            settingsRoute,
            projectsRoute,
            { path: "signup", middleware: [navigateToModules] },
            { index: true, middleware: [navigateToModules] },
            moduleSetRoute,
            deliveryRoute,
            inviteRoute,
            recoverRoute,
            { path: "*", Component: NotFoundPage },
          ],
        },
      ],

      // I think this prevents suspense from delaying the reaction to route changes?
      // It also shuts up a useless warning in dev.
      HydrateFallback: () => null,
    },
  ],
  {
    getContext,
  },
);

interface LocationTrackingErrorBoundaryProps {
  // Seems like Router type isn't exported from react-router?
  // They export *a* `Router`, but it's a React component type.
  router: DataRouter;
  // router: ReturnType<typeof createBrowserRouter>;
  children: ReactNode;
}

// Wrap ErrorBoundary so it tracks the router location so back/forward resets error state
const LocationTrackingErrorBoundary = ({ router, children }: LocationTrackingErrorBoundaryProps) => {
  const [key, setKey] = useState<string>(router.state.location.key);

  useEffect(() => {
    return router.subscribe((state) => {
      setKey(state.location.key);
    });
  }, [router]);

  return <ErrorBoundary resetKeys={[key]}>{children}</ErrorBoundary>;
};
